import { z } from 'zod';
import { idSchema, isoDateSchema } from '../primitives/branded.js';
import { timestampSchema } from '../primitives/timestamp.js';
/**
 * Backfill/refresh bookkeeping.
 *
 * The free Twelve Data tier allows 8 requests per minute and 800 credits per day,
 * so a full history import is not guaranteed to finish in one run. Progress is
 * persisted per instrument and the job resumes from `status` on the next start.
 */
export const syncStatusSchema = z.enum(['pending', 'in_progress', 'complete', 'failed']);
export const syncStateSchema = z.object({
    instrumentId: idSchema,
    status: syncStatusSchema,
    /** Oldest and newest bar stored so far. */
    earliestBar: isoDateSchema.nullable(),
    latestBar: isoDateSchema.nullable(),
    /** Set once the provider reports no further history before `earliestBar`. */
    historyComplete: z.boolean(),
    lastSyncedAt: timestampSchema.nullable(),
    lastError: z.string().nullable(),
    /** Consecutive failures, used for exponential backoff. */
    failureCount: z.number().int().nonnegative(),
    updatedAt: timestampSchema,
});
/** Result of a sync run, returned by the API and logged by the scheduler. */
export const syncReportSchema = z.object({
    startedAt: timestampSchema,
    finishedAt: timestampSchema,
    instrumentsProcessed: z.number().int().nonnegative(),
    barsInserted: z.number().int().nonnegative(),
    requestsUsed: z.number().int().nonnegative(),
    /** True when the run stopped early because the API budget was exhausted. */
    budgetExhausted: z.boolean(),
    errors: z.array(z.object({ symbol: z.string(), message: z.string() })),
});
export const triggerSyncSchema = z.object({
    /** Limit the run to specific instruments; empty means "all active positions". */
    instrumentIds: z.array(idSchema).optional(),
    /** Import full history instead of only the missing tail. */
    mode: z.enum(['incremental', 'backfill']).default('incremental'),
    force: z.boolean().default(false),
});
//# sourceMappingURL=sync.js.map